<script setup lang="ts">
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import {
    AugmentationVariantEnum,
    type CustomerCaseDataChange as CaseDataChange,
    ChangeSourceEnum,
    type CustomerEntityChange as EntityChange
  } from '@/types/generated/types.gen'
  import {
    Calendar,
    FileText,
    Mail,
    MessageCircleQuestion,
    MessageSquare,
    Phone
  } from 'lucide-vue-next'
  import { computed, ref, type PropType } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps({
    change: {
      type: Object as PropType<EntityChange | CaseDataChange | null>,
      required: false
    }
  })

  const { t } = useTranslation({
    en: {
      questionnaire: 'Questionnaire',
      appointment: 'Appointment Booking',
      pdfForm: 'PDF Form',
      ai: 'AI',
      email: 'E-Mail',
      call: 'Call',
      chatbot: 'Chatbot',
      system: 'SYSTEM',
      page: 'Page',
      deletedDocument: '[DELETED]'
    },
    de: {
      questionnaire: 'Fragebogen',
      appointment: 'Terminbuchung',
      pdfForm: 'PDF Formular',
      ai: 'KI',
      email: 'E-Mail',
      call: 'Anruf',
      chatbot: 'Chatbot',
      system: 'SYSTEM',
      page: 'Seite',
      deletedDocument: '[GELÖSCHT]'
    }
  })

  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const initialPage = computed(() => {
    const pages = props.change?.ai_augmentation?.referenced_document_pages
    if (pages && pages.length > 0) {
      return pages[0]
    }
    return 1
  })
  const aiAugmentationDocument = computed(() => {
    if (!props.change?.ai_augmentation) {
      return null
    }
    return props.change.ai_augmentation.document || null
  })

  const isDocumentDeleted = computed(() => {
    return aiAugmentationDocument.value?.is_deleted
  })

  const shortenDocumentTitle = (title: string) => {
    if (title.length > 20) {
      return title.slice(0, 12) + '[…].pdf'
    }
    return title
  }

  const documentDisplayTitle = computed(() => {
    if (aiAugmentationDocument.value?.filename) {
      return shortenDocumentTitle(aiAugmentationDocument.value.filename)
    }
    if (aiAugmentationDocument.value?.title) {
      return shortenDocumentTitle(aiAugmentationDocument.value.title)
    }
    return null
  })

  const deletedDocumentDisplayTitle = computed(() => {
    if (isDocumentDeleted.value) {
      return t('deletedDocument') + ' ' + documentDisplayTitle.value
    }
    return null
  })

  function openPdf() {
    const document = props.change?.ai_augmentation?.document
    if (document) {
      pdfUrl.value = document.url
      isPdfOpen.value = true
    }
  }
</script>
<template>
  <div v-if="change">
    <span
      v-if="change.change_source === ChangeSourceEnum.USER"
      class="flex items-center gap-2"
    >
      <UserAvatar
        :user="change.user"
        size="26px"
      />
      {{ change.user.first_name }} {{ change.user.last_name }}
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.QUESTIONNAIRE"
      class="flex items-center gap-2"
    >
      <MessageCircleQuestion class="h-4 w-4 text-neutral-600" />
      {{ t('questionnaire') }}
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.APPOINTMENT"
      class="flex items-center gap-2"
    >
      <Calendar class="h-4 w-4 text-neutral-600" />
      {{ t('appointment') }}
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.PDF_FORM"
      class="flex items-center gap-2"
    >
      <FileText class="h-4 w-4 text-neutral-600" />
      {{ t('pdfForm') }}
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.AI"
      class="flex items-center gap-2"
    >
      <span
        v-if="
          change.ai_augmentation?.augmentation_variant === AugmentationVariantEnum.DOCUMENT_ANALYSIS
        "
        class="flex items-center gap-2"
      >
        <button
          class="group inline-flex cursor-pointer items-center space-x-1 text-neutral-800"
          :class="!isDocumentDeleted ? 'underline hover:text-purple-700' : ''"
          @click="!isDocumentDeleted ? openPdf() : null"
        >
          <FileText
            class="h-4 w-4 text-neutral-600"
            :class="!isDocumentDeleted ? 'text-purple-700' : 'cursor-default text-neutral-600'"
          />
          <span :class="isDocumentDeleted ? 'cursor-default text-neutral-600' : ''">
            <span v-if="!isDocumentDeleted">
              {{ documentDisplayTitle }}
            </span>
            <span v-else>
              {{ deletedDocumentDisplayTitle }}
            </span>
            <span
              v-for="page in change.ai_augmentation?.referenced_document_pages"
              :key="page"
              class="ml-1 inline-flex items-center justify-center rounded-full bg-purple-100 px-2 py-0.5 text-xs
                font-medium text-purple-700"
              :title="`${t('page')} ${page}`"
            >
              {{ t('page').charAt(0) }}. {{ page }}
            </span>
          </span>
        </button>
        <span
          v-if="change.ai_augmentation"
          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
            text-purple-700"
        >
          {{ t('ai') }}
        </span>
      </span>
      <span
        v-else-if="
          change.ai_augmentation?.augmentation_variant === AugmentationVariantEnum.EMAIL_EXTRACTION
        "
        class="flex inline-flex items-center gap-2"
      >
        <Mail class="h-4 w-4 text-neutral-600" />
        {{ t('email') }}
        <span
          v-if="change.ai_augmentation"
          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
            text-purple-700"
        >
          {{ t('ai') }}
        </span>
      </span>
      <span
        v-else-if="change.ai_augmentation?.augmentation_variant === AugmentationVariantEnum.CALL"
        class="flex items-center gap-2 text-neutral-800"
      >
        <Phone class="h-4 w-4 text-neutral-600" />
        {{ t('call') }}
        <span
          v-if="change.ai_augmentation"
          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
            text-purple-700"
        >
          {{ t('ai') }}
        </span>
      </span>
      <span
        v-else-if="change.ai_augmentation?.augmentation_variant === AugmentationVariantEnum.CHATBOT"
        class="flex items-center gap-2"
      >
        <MessageSquare class="h-4 w-4 text-neutral-600" />
        {{ t('chatbot') }}
        <span
          v-if="change.ai_augmentation"
          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
            text-purple-700"
        >
          {{ t('ai') }}
        </span>
      </span>
    </span>
    <span v-else-if="change.change_source === ChangeSourceEnum.SYSTEM">{{ t('system') }}</span>
    <PDFViewer
      v-if="change?.ai_augmentation?.document"
      v-model:isOpen="isPdfOpen"
      v-model:url="pdfUrl"
      :filename="change?.ai_augmentation?.document?.filename"
      :initial-page="initialPage"
      :scale="1.0"
      :document="change?.ai_augmentation?.document"
    />
  </div>
</template>
